import React, { useState, useRef, useEffect } from 'react';

interface ServiceSubCheckboxProps {
  titulo: string;
  placeholder: string;
  value: string[];
  onChange: (values: string[]) => void;
  options: { value: string; label: string; subOptions?: { value: string; label: string }[] }[];
}

const ServiceSubCheckbox: React.FC<ServiceSubCheckboxProps> = ({
  titulo,
  placeholder,
  value,
  onChange,
  options
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedServices, setExpandedServices] = useState<Set<string>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Sub-opções vêm das options recebidas (API)
  const getSubOptions = (serviceValue: string) => {
    return options.find((o) => o.value === serviceValue)?.subOptions ?? [];
  };

  const mainCategories = options.map((o) => o.value);

  // Função para verificar se todas as sub-opções de um serviço estão selecionadas
  const areAllSubOptionsSelected = (serviceValue: string) => {
    const subOptions = getSubOptions(serviceValue);
    if (subOptions.length === 0) return false;

    return subOptions.every(subOption => value.includes(subOption.value));
  };

  // Função para verificar se algumas sub-opções de um serviço estão selecionadas
  const areSomeSubOptionsSelected = (serviceValue: string) => {
    const subOptions = getSubOptions(serviceValue);
    if (subOptions.length === 0) return false;

    return subOptions.some(subOption => value.includes(subOption.value)) && !areAllSubOptionsSelected(serviceValue);
  };

  // Função para lidar com toggle de serviço principal
  const handleServiceToggle = (serviceValue: string) => {
    const subOptions = getSubOptions(serviceValue);
    const isServiceSelected = value.includes(serviceValue);

    if (subOptions.length > 0) {
      // Se o serviço tem sub-opções, verificar se todas já estão selecionadas
      const allSubsSelected = areAllSubOptionsSelected(serviceValue);
      
      if (allSubsSelected || isServiceSelected) {
        // Se todas as sub-opções já estão selecionadas OU o serviço está selecionado,
        // desmarcar serviço e todas suas sub-opções
        const newValues = value.filter(item =>
          item !== serviceValue && !subOptions.some(sub => sub.value === item)
        );
        onChange(newValues);
      } else {
        // Caso contrário, marcar serviço e todas suas sub-opções
        const newValues = [
          ...value.filter(item => !subOptions.some(sub => sub.value === item)),
          serviceValue,
          ...subOptions.map(sub => sub.value)
        ];
        onChange(newValues);
      }
    } else {
      // Se não tem sub-opções, comportamento normal
      if (isServiceSelected) {
        // Garantir que sub-opções de categorias com mesmo nome não permaneçam
        const newValues = value.filter(item => item !== serviceValue);
        onChange(newValues);
      } else {
        onChange([...value, serviceValue]);
      }
    }
  };

  // Função para lidar com toggle de sub-opção
  const handleSubOptionToggle = (serviceValue: string, subOptionValue: string) => {
    const subOptions = getSubOptions(serviceValue);
    const isSubOptionSelected = value.includes(subOptionValue);
    const isServiceSelected = value.includes(serviceValue);

    if (isSubOptionSelected) {
      // Desmarcar sub-opção
      const newValues = value.filter(item => item !== subOptionValue);

      // Sempre remover a categoria pai quando uma sub-opção é desmarcada
      // para evitar filtragem ampla quando apenas sub-opções específicas são selecionadas
      if (isServiceSelected) {
        const index = newValues.indexOf(serviceValue);
        if (index !== -1) {
          newValues.splice(index, 1);
        }
      }

      onChange(newValues);
    } else {
      // Marcar sub-opção
      const newValues = [...value, subOptionValue];

      // Se todas as sub-opções estiverem selecionadas, marcar também o serviço
      const allSubOptionsSelected = subOptions.every(sub => newValues.includes(sub.value));
      if (allSubOptionsSelected && !isServiceSelected) {
        newValues.push(serviceValue);
      }

      onChange(newValues);
    }
  };

  // Função para expandir/colapsar sub-opções
  const toggleServiceExpansion = (serviceValue: string, event: React.MouseEvent) => {
    event.stopPropagation();
    const newExpanded = new Set(expandedServices);
    if (newExpanded.has(serviceValue)) {
      newExpanded.delete(serviceValue);
    } else {
      newExpanded.add(serviceValue);
    }
    setExpandedServices(newExpanded);
  };

  const handleInputClick = () => {
    setIsOpen(!isOpen);
  };


  // Função para limpar categorias principais "quebradas" (que não têm mais todas as subcategorias)
  // ATENÇÃO: Esta função NÃO deve chamar onChange pois é chamada durante renderização
  const cleanBrokenCategories = () => {
    let cleanedValue = [...value];

    mainCategories.forEach(category => {
      const isCategorySelected = cleanedValue.includes(category);
      const allSubsSelected = areAllSubOptionsSelected(category);

      // Se a categoria está selecionada mas não tem todas as subcategorias,
      // ela deve ser removida (estado inconsistente) apenas para exibição
      if (isCategorySelected && !allSubsSelected) {
        cleanedValue = cleanedValue.filter(item => item !== category);
      }
    });

    // Retorna os valores limpos apenas para exibição, SEM atualizar o estado
    return cleanedValue;
  };

  // Função para obter os chips a serem exibidos (considerando a lógica de categoria principal)
  const getChipsToDisplay = () => {
    // Primeiro, limpar qualquer categoria principal "quebrada"
    const cleanValue = cleanBrokenCategories();

    const displayChips: string[] = [];
    const processedSubOptions = new Set<string>();

    const categoriesDirectlySelected: string[] = [];
    const categoriesWithAllSubsSelected: string[] = [];

    mainCategories.forEach(category => {
      const isCategorySelected = cleanValue.includes(category);
      const allSubsSelected = areAllSubOptionsSelected(category);

      if (isCategorySelected && allSubsSelected) {
        // Categoria foi selecionada diretamente E tem todas as subcategorias
        categoriesDirectlySelected.push(category);
        // Marcar todas as subcategorias como processadas
        const subOptions = getSubOptions(category);
        subOptions.forEach(sub => processedSubOptions.add(sub.value));
      } else if (allSubsSelected && !isCategorySelected) {
        // Categoria tem todas as subcategorias selecionadas, mas não foi diretamente selecionada
        // (usuário selecionou subcategorias individualmente)
        categoriesWithAllSubsSelected.push(category);
        // Marcar todas as subcategorias como processadas
        const subOptions = getSubOptions(category);
        subOptions.forEach(sub => processedSubOptions.add(sub.value));
      }
    });

    // Prioridade 1: Categorias diretamente selecionadas (mesmo que tenha todas as subcategorias)
    categoriesDirectlySelected.forEach(category => {
      displayChips.push(category);
    });

    // Prioridade 2: Categorias que têm todas as subcategorias selecionadas (mas não foram diretamente selecionadas)
    categoriesWithAllSubsSelected.forEach(category => {
      displayChips.push(category);
    });

    // Prioridade 3: Subcategorias que não fazem parte de categorias processadas
    cleanValue.forEach(item => {
      if (!processedSubOptions.has(item) &&
          !categoriesDirectlySelected.includes(item) &&
          !categoriesWithAllSubsSelected.includes(item)) {
        displayChips.push(item);
      }
    });

    return displayChips;
  };

  const handleRemoveChip = (optionValue: string) => {
    if (mainCategories.includes(optionValue)) {
      const subOptions = getSubOptions(optionValue);
      const newValues = value.filter(item => item !== optionValue && !subOptions.some(sub => sub.value === item));
      onChange(newValues);
      return;
    }

    const owningOption = options.find((o) => o.subOptions?.some((sub) => sub.value === optionValue));
    let newValues = value.filter(item => item !== optionValue);
    if (owningOption && newValues.includes(owningOption.value)) {
      const remainingSubs = (owningOption.subOptions ?? []).filter(sub => newValues.includes(sub.value));
      if (remainingSubs.length === 0) {
        newValues = newValues.filter(item => item !== owningOption.value);
      }
    }
    onChange(newValues);
  };

  const getDisplayText = () => {
    if (value.length === 0) return placeholder;
    return ''; // Não mostra texto quando há chips
  };

  const getOptionLabel = (optionValue: string) => {
    // Primeiro tenta encontrar como categoria principal
    const serviceOption = options.find(opt => opt.value === optionValue);
    if (serviceOption) return serviceOption.label;
    
    // Depois busca nas sub-opções
    for (const opt of options) {
      const sub = opt.subOptions?.find(s => s.value === optionValue);
      if (sub) return sub.label;
    }
    
    // Se não encontrar, pode ser um valor antigo sem prefixo - retornar como está
    return optionValue;
  };

  return (
    <div style={{ position: 'relative' }}>
      <label style={{
        fontSize: '0.875rem',
        fontWeight: '600',
        color: 'var(--c2d3748)',
        marginBottom: '0.5rem',
        display: 'block'
      }}>
        {titulo}
      </label>

      <div ref={containerRef} style={{ position: 'relative' }}>
        {/* Campo principal */}
        <div
          onClick={handleInputClick}
          style={{
            width: '100%',
            minHeight: value.length > 0 && getChipsToDisplay().length > 5 ? '56px' : '48px',
            padding: value.length > 0 ? '8px 1rem' : '0 1rem',
            border: '1px solid var(--ce2e8f0)',
            borderRadius: '6px',
            fontSize: '0.875rem',
            fontFamily: 'inherit',
            cursor: 'pointer',
            display: 'flex',
            alignItems: value.length > 0 ? 'flex-start' : 'center',
            backgroundColor: 'var(--surface-card)',
            color: value.length === 0 ? 'var(--ca0aec0)' : 'var(--c2d3748)',
            flexWrap: 'wrap',
            gap: '8px'
          }}
        >
          {/* Chips dos itens selecionados */}
          {value.length > 0 && (() => {
            const chipsToDisplay = getChipsToDisplay();
            const hasMoreThan5 = chipsToDisplay.length > 5;
            const chipsToShow = chipsToDisplay.slice(0, 5);
            const remainingCount = chipsToDisplay.length - 5;

            return (
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                flex: 1,
                alignItems: 'center'
              }}>
                {/* Mostrar apenas os primeiros 5 chips */}
                {chipsToShow.map((selectedValue) => {
                  const label = getOptionLabel(selectedValue);
                  return (
                    <div
                      key={selectedValue}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: 'var(--cf7fafc)',
                        border: '1px solid var(--ce2e8f0)',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        fontSize: '0.875rem',
                        color: 'var(--c4a5568)',
                        gap: '6px'
                      }}
                      onClick={(e) => e.stopPropagation()} // Evita abrir o dropdown ao clicar no chip
                    >
                      <span>{label}</span>
                      <button
                        onClick={() => handleRemoveChip(selectedValue)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'var(--c718096)',
                          cursor: 'pointer',
                          padding: '0',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '16px',
                          height: '16px'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = '#e53e3e';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = '#718096';
                        }}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}

                {/* Mostrar chip "+X mais" se houver mais de 5 chips a serem exibidos */}
                {hasMoreThan5 && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: 'var(--cf7fafc)',
                      border: '1px solid var(--ce2e8f0)',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '0.875rem',
                      color: 'var(--c4a5568)',
                      gap: '6px',
                      cursor: 'pointer'
                    }}
                    onClick={(e) => e.stopPropagation()}
                    title={`+${remainingCount} mais selecionado(s)`}
                  >
                    <span>+{remainingCount} mais</span>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Texto placeholder quando não há seleções */}
          {value.length === 0 && (
            <span style={{ flex: 1 }}>{getDisplayText()}</span>
          )}

          {/* Seta do dropdown */}
          <div style={{
            color: 'var(--c718096)',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
            alignSelf: value.length > 0 ? 'flex-start' : 'center',
            marginTop: value.length > 0 ? '8px' : '0'
          }}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
              <path d="M6 8L2 4h8L6 8z" />
            </svg>
          </div>
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'var(--surface-card)',
            border: '1px solid var(--ce2e8f0)',
            borderRadius: '6px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            zIndex: 1000,
            maxHeight: '400px',
            overflowY: 'auto'
          }}>
            <div>
              {options.map((option, index) => {
                const isServiceSelected = value.includes(option.value);
                const subOptions = getSubOptions(option.value);
                const hasSubOptions = subOptions.length > 0;
                const isExpanded = expandedServices.has(option.value);

                return (
                  <div key={index}>
                    {/* Serviço principal */}
                    <div
                      style={{
                        padding: '12px 16px',
                        borderBottom: '1px solid var(--cf7fafc)',
                        backgroundColor: 'transparent',
                        transition: 'background-color 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--ce2e8f0)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      {/* Container da checkbox - apenas marca/desmarca */}
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          handleServiceToggle(option.value);
                        }}
                        style={{
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '8px 12px',
                          margin: '-8px -12px',
                          borderRadius: '4px',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--ce2e8f0)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <div style={{
                          width: '22px',
                          height: '22px',
                          border: `2px solid ${isServiceSelected ? 'var(--ce52940)' : 'var(--c798891)'}`,
                          borderRadius: '3px',
                          backgroundColor: isServiceSelected ? 'var(--ce52940)' : 'var(--surface-input)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease',
                          position: 'relative'
                        }}>
                          {isServiceSelected && (
                            <span style={{
                              color: 'var(--ink-white)',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              opacity: 1,
                              transition: 'opacity 0.2s'
                            }}>
                              ✔
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Container do título e expansão - expande/colapsa subcategorias */}
                      <div
                        onClick={(e) => {
                          if (hasSubOptions) {
                            toggleServiceExpansion(option.value, e);
                          }
                        }}
                        style={{
                          flex: 1,
                          cursor: hasSubOptions ? 'pointer' : 'default',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px 0',
                          margin: '-12px 0'
                        }}
                      >
                        {/* Label do serviço */}
                        <span style={{
                          fontSize: '0.875rem',
                          color: 'var(--c2d3748)'
                        }}>
                          {option.label}
                        </span>

                        {/* Indicador de expansão se houver sub-opções */}
                        {hasSubOptions && (
                          <div
                            style={{
                              color: 'var(--c718096)',
                              transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s ease',
                              marginLeft: '8px'
                            }}
                          >
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                              <path d="M6 8L2 4h8L6 8z" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Sub-opções */}
                    {hasSubOptions && isExpanded && (
                      <div style={{ backgroundColor: 'var(--cf8f9fa)' }}>
                        {subOptions.map((subOption, subIndex) => {
                          const isSubOptionSelected = value.includes(subOption.value);
                          return (
                            <div
                              key={subIndex}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSubOptionToggle(option.value, subOption.value);
                              }}
                              style={{
                                padding: '8px 16px 8px 40px',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                color: 'var(--c2d3748)',
                                borderBottom: subIndex < subOptions.length - 1 ? '1px solid var(--ce2e8f0)' : 'none',
                                backgroundColor: 'transparent',
                                transition: 'background-color 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                width: '100%'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--ce2e8f0)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              {/* Container da checkbox da sub-opção */}
                              <div style={{
                                width: '18px',
                                height: '18px',
                                border: `2px solid ${isSubOptionSelected ? 'var(--ce52940)' : 'var(--c798891)'}`,
                                borderRadius: '3px',
                                backgroundColor: isSubOptionSelected ? 'var(--ce52940)' : 'var(--surface-input)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s ease',
                                flexShrink: 0
                              }}>
                                {isSubOptionSelected && (
                                  <span style={{
                                    color: 'var(--ink-white)',
                                    fontSize: '12px',
                                    fontWeight: 'bold'
                                  }}>
                                    ✔
                                  </span>
                                )}
                              </div>

                              {/* Label da sub-opção - ocupa todo o espaço restante */}
                              <span style={{
                                flex: 1,
                                padding: '2px 0' // Pequeno padding para melhorar a área clicável
                              }}>
                                {subOption.label}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceSubCheckbox;
